import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Oekt } from "../state/session";
import * as sl from "../state/shotlist";
import { useOmstokking } from "../components/omstokking";
import { useLevende } from "../state/levende";

/**
 * Shotlista: scenene til venstre, bildene til høyre.
 *
 * IKKE EN TEKSTBOKS. Beskjedene er prosa man leser; shotlista er en liste
 * man jobber seg gjennom. Da skal den oppføre seg som en liste: man trykker
 * på en linje og skriver, og neste linje er ett tastetrykk unna.
 *
 * SCENEN ER MAPPA. En opptaksdag er flere settinger, og foto tenker i dem —
 * «kjøkkenet», «utenfor fjøset». Med scenene i egen kolonne er dagen lesbar
 * i ett blikk, og man slipper å bla gjennom alt for å finne det man rigger
 * for nå.
 *
 * KOMMENTAR OG REFERANSEBILDE HØRER TIL BILDET. Kommentaren står som
 * brødtekst rett under hovedlinja — ikke bak en knapp man må åpne, for da
 * blir den ikke skrevet. Referansebildet er det raskeste språket som finnes
 * mellom to fotografer.
 *
 * LAGRES AV SEG SELV, som beskjedene. Teksten går opp når man forlater
 * linja; haken og bildet med én gang.
 */

/**
 * Prefiks paa id-en til en rad som staar paa skjermen, men ennaa ikke i
 * basen. Den skal se ut som alle andre — bare ikke skrives til.
 */
const UFERDIG = "uferdig:";

export function ShotlistScreen({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const produksjonId = oekt.production.id;
  const opptaksdag = oekt.shootDay;
  const [data, setData] = useState<sl.Shotliste>(sl.TOM);
  const [laster, setLaster] = useState(true);
  const [valgt, setValgt] = useState<string | null>(null);
  const [nyScene, setNyScene] = useState<string | null>(null);
  const [nyttShot, setNyttShot] = useState("");
  const [aapen, setAapen] = useState<string | null>(null);
  const [opplasting, setOpplasting] = useState<{ id: string; feil: boolean } | null>(null);
  const [bildeversjon, setBildeversjon] = useState<Readonly<Record<string, number>>>({});
  /* «Fjern alle merkede» spør i seg selv. Se knappen nederst i kolonnen. */
  const [bekrefter, setBekrefter] = useState(false);
  /* Scenen som har papirkurven trykket og venter på svar. Samme form som
     over: raden bytter seg selv ut med et ja og et nei. */
  const [sceneBekreft, setSceneBekreft] = useState<string | null>(null);
  const skriver = useRef(false);
  /* Teksten i den tomme linja, lest av verktøyet uten å gå veien om en
     lukking som kan være et bilde gammel. */
  const utkastRef = useRef("");
  utkastRef.current = nyttShot;
  /* Raden den tomme linja ble til. Holder blur og verktøyknappene fra å
     lage HVER SIN rad av det samme utkastet. */
  const nyRef = useRef<sl.Shot | null>(null);

  const last = useCallback((): void => {
    void (async () => {
      const d = await sl.hent(produksjonId, opptaksdag);
      setData(d);
      setValgt((v) => (v !== null && d.scener.some((x) => x.id === v) ? v : d.scener[0]?.id ?? null));
      setLaster(false);
    })();
  }, [produksjonId, opptaksdag]);

  useEffect(last, [last]);

  /* Legger A-foto inn bilder mens produsenten ser på, dukker de opp. */
  useLevende("shotlist", ["shot_scene", "shot"], last);

  const perScene = useMemo(() => {
    const m = new Map<string, sl.Shot[]>();
    for (const s of data.scener) m.set(s.id, []);
    for (const b of data.shots) m.get(b.sceneId)?.push(b);
    return m;
  }, [data]);

  const scene = data.scener.find((s) => s.id === valgt) ?? null;
  const mine = valgt === null ? [] : perScene.get(valgt) ?? [];

  /* ---- scener ---- */

  const lagScene = useCallback(async (navn: string): Promise<void> => {
    const n = navn.trim();
    setNyScene(null);
    if (n.length === 0) return;
    const s = await sl.nyScene(produksjonId, opptaksdag, n, data.scener.length);
    if (s === null) return;
    setData((p) => ({ ...p, scener: [...p.scener, s] }));
    setValgt(s.id);
  }, [produksjonId, opptaksdag, data.scener.length]);

  const dopScene = useCallback(async (id: string, navn: string): Promise<void> => {
    const n = navn.trim();
    const foer = data.scener.find((s) => s.id === id);
    if (foer === undefined || n.length === 0 || n === foer.navn) return;
    setData((p) => ({
      ...p, scener: p.scener.map((s) => (s.id === id ? { ...s, navn: n } : s)),
    }));
    if (!await sl.endreScene(id, n)) {
      setData((p) => ({ ...p, scener: p.scener.map((s) => (s.id === id ? foer : s)) }));
    }
  }, [data.scener]);

  const slettScene = useCallback(async (id: string): Promise<void> => {
    const s = data.scener.find((x) => x.id === id);
    if (s === undefined) return;
    setSceneBekreft(null);
    const foer = data;
    setData((p) => ({
      scener: p.scener.filter((x) => x.id !== id),
      shots: p.shots.filter((x) => x.sceneId !== id),
    }));
    setValgt((v) => (v === id ? data.scener.find((x) => x.id !== id)?.id ?? null : v));
    if (!await sl.fjernScene(id)) {
      setData(foer);
      return;
    }
    /* `on delete cascade` tar radene. Filene kjenner ikke til scenen, saa de
       maa fjernes her — ellers blir de liggende uten noe som peker paa dem. */
    await sl.fjernBilder((perScene.get(id) ?? []).map((b) => b.bildeSti));
  }, [data, perScene]);

  /* ---- bilder ---- */

  /**
   * Legger til et bilde. Returnerer raden, slik at verktøyet på den tomme
   * linja kan jobbe videre med den som nettopp ble laget.
   *
   * `tom` slipper gjennom en linje uten tekst: trykker man «Referansebilde»
   * før man har skrevet noe, er det bildet som er poenget — og teksten kan
   * komme etterpå.
   */
  const leggTil = useCallback(async (
    tekst: string, tom = false,
  ): Promise<sl.Shot | null> => {
    const t = tekst.trim();
    if (skriver.current || valgt === null || (t.length === 0 && !tom)) return null;
    skriver.current = true;

    /*
     * LINJA STAAR MED EN GANG.
     *
     * Her laa blinket: teksten ble toemt fra den tomme linja, og saa var den
     * ingen steder mens raden ble skrevet til basen — plassholderen «Nytt
     * bilde» sto der et oeyeblikk foer linja dukket opp i lista. Nå settes
     * raden inn foerst, med en midlertidig id, og byttes ut med den som
     * kommer tilbake.
     */
    const midlertidig: sl.Shot = {
      id: `${UFERDIG}${Date.now()}`,
      sceneId: valgt,
      tekst: t,
      notat: "",
      bildeSti: null,
      gjort: false,
      sortOrder: mine.length,
    };
    setNyttShot("");
    setData((p) => ({ ...p, shots: [...p.shots, midlertidig] }));

    const b = await sl.nyttShot(valgt, t, mine.length);
    skriver.current = false;
    if (b === null) {
      // Teksten kommer tilbake. Ingenting skal gaa tapt fordi nettet svikter.
      setData((p) => ({ ...p, shots: p.shots.filter((x) => x.id !== midlertidig.id) }));
      setNyttShot(t);
      return null;
    }
    setData((p) => ({ ...p, shots: p.shots.map((x) => (x.id === midlertidig.id ? b : x)) }));
    return b;
  }, [valgt, mine.length]);

  /**
   * Gjør utkastet til en rad — én gang.
   *
   * Både blur og verktøyknappene går hit. Uten at den husker hva den
   * nettopp lagde, ville et trykk på «Referansebilde» først utløst blur
   * (som lager raden) og deretter laget en til.
   */
  const materialiser = useCallback(async (): Promise<sl.Shot | null> => {
    if (nyRef.current !== null) return nyRef.current;
    const b = await leggTil(utkastRef.current, true);
    nyRef.current = b;
    return b;
  }, [leggTil]);

  const endre = useCallback(async (
    id: string, endring: Partial<{ tekst: string; notat: string; gjort: boolean; bildeSti: string | null }>,
  ): Promise<void> => {
    // En rad som ikke har naadd basen ennaa kan ikke oppdateres der.
    if (id.startsWith(UFERDIG)) return;
    const foer = data.shots.find((b) => b.id === id);
    if (foer === undefined) return;
    setData((p) => ({
      ...p, shots: p.shots.map((b) => (b.id === id ? { ...b, ...endring } : b)),
    }));
    if (!await sl.endreShot(id, endring)) {
      setData((p) => ({ ...p, shots: p.shots.map((b) => (b.id === id ? foer : b)) }));
    }
  }, [data.shots]);

  /**
   * Huker av hele scenen — eller tar merkingen av igjen.
   *
   * Boksen står ved tittelen, på samme loddrette linje som de andre, fordi
   * den gjør det samme som dem: den merker. En bryter et annet sted hadde
   * vært en annen handling.
   */
  const merkAlle = useCallback(async (gjort: boolean): Promise<void> => {
    const ider = mine.map((b) => b.id);
    if (ider.length === 0) return;
    const foer = data.shots;
    setData((p) => ({
      ...p,
      shots: p.shots.map((b) => (ider.includes(b.id) ? { ...b, gjort } : b)),
    }));
    if (!await sl.settAlleGjort(ider, gjort)) setData((p) => ({ ...p, shots: foer }));
  }, [mine, data.shots]);

  /** Alt som er merket, i hele dagen — ikke bare i scenen man står i. */
  const merkede = useMemo(() => data.shots.filter((b) => b.gjort), [data.shots]);

  /* Staar ikke armert naar det ikke er noe aa fjerne — og ikke paa tvers av
     et scenebytte heller. Et spoersmaal som blir haengende er et spoersmaal
     man svarer ja paa ved et uhell. */
  useEffect(() => { setBekrefter(false); }, [valgt, merkede.length]);
  useEffect(() => { setSceneBekreft(null); }, [valgt]);

  useEffect(() => {
    if (!bekrefter && sceneBekreft === null) return;
    const av = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      setBekrefter(false);
      setSceneBekreft(null);
    };
    window.addEventListener("keydown", av);
    return () => { window.removeEventListener("keydown", av); };
  }, [bekrefter, sceneBekreft]);


  const fjernMerkede = useCallback(async (): Promise<void> => {
    if (merkede.length === 0) return;
    setBekrefter(false);
    const ider = merkede.map((b) => b.id);
    const foer = data.shots;
    setData((p) => ({ ...p, shots: p.shots.filter((b) => !ider.includes(b.id)) }));
    if (!await sl.fjernShots(ider)) {
      setData((p) => ({ ...p, shots: foer }));
      return;
    }
    await sl.fjernBilder(merkede.map((b) => b.bildeSti));
  }, [merkede, data.shots]);

  const slettShot = useCallback(async (id: string): Promise<void> => {
    const foer = data.shots;
    const sti = foer.find((b) => b.id === id)?.bildeSti ?? null;
    setData((p) => ({ ...p, shots: p.shots.filter((b) => b.id !== id) }));
    if (!await sl.fjernShot(id)) {
      setData((p) => ({ ...p, shots: foer }));
      return;
    }
    await sl.fjernBilder([sti]);
  }, [data.shots]);

  /**
   * Laster opp et referansebilde.
   *
   * SIER FRA NÅR DET FEILER. En opplasting som bare blir borte er verre enn
   * en som ikke finnes: man tror bildet ligger der, og oppdager det først
   * når noen rigger feil.
   */
  const lastOpp = useCallback(async (fil: File, id: string): Promise<void> => {
    setOpplasting({ id, feil: false });
    const sti = await sl.lastOppBilde(produksjonId, id, fil);
    if (sti === null) {
      setOpplasting({ id, feil: true });
      return;
    }
    const ok = await sl.endreShot(id, { bildeSti: sti });
    setOpplasting(ok ? null : { id, feil: true });
    if (!ok) return;
    setData((p) => ({
      ...p, shots: p.shots.map((b) => (b.id === id ? { ...b, bildeSti: sti } : b)),
    }));
    /* Byttes bildet, er stien den samme — og da ville lenka ikke blitt hentet
       på nytt. Telleren tvinger den fram. */
    setBildeversjon((v) => ({ ...v, [id]: (v[id] ?? 0) + 1 }));
  }, [produksjonId]);

  /*
   * OMSTOKKING, samme regnestykke som sidelista i appen.
   *
   * Scenene stokkes i kolonnen, bildene i lista. Rekkefølgen skrives til
   * `sort_order` for de radene som faktisk flyttet seg — ikke for alle, og
   * ikke med en ny spørring per piksel.
   */
  const flyttScene = useCallback((fra: number, til: number): void => {
    const neste = data.scener.slice();
    const [flyttet] = neste.splice(fra, 1);
    if (flyttet === undefined) return;
    neste.splice(til, 0, flyttet);
    setData((p) => ({ ...p, scener: neste }));
    void sl.settRekkefolge("shot_scene", neste.map((x) => x.id));
  }, [data.scener]);

  const flyttShot = useCallback((fra: number, til: number): void => {
    if (valgt === null) return;
    const rekke = mine.slice();
    const [flyttet] = rekke.splice(fra, 1);
    if (flyttet === undefined) return;
    rekke.splice(til, 0, flyttet);
    setData((p) => ({
      ...p,
      shots: [...p.shots.filter((b) => b.sceneId !== valgt), ...rekke],
    }));
    void sl.settRekkefolge("shot", rekke.map((x) => x.id));
  }, [valgt, mine]);

  const sceneStokk = useOmstokking(data.scener.map((x) => x.id), flyttScene);
  const shotStokk = useOmstokking(mine.map((x) => x.id), flyttShot);

  const skall = (innhold: JSX.Element): JSX.Element => (
    <div className="flate">
      <div className="flate__innhold">{innhold}</div>
    </div>
  );

  if (laster) return skall(<p className="synk__melding">Henter shotlista …</p>);

  return skall(
    <div className="shot">
      <aside className="shot__scener">
        {/*
          Over scenene, ikke inne i én av dem: den rydder hele dagen.

          SPØRSMÅLET STILLES I KNAPPEN. En nettleserdialog river deg ut av
          flata, lander midt på skjermen langt fra det du pekte på, og ser
          ut som den kommer fra noe annet enn siden du står i. Her skjer
          svaret der handlingen ble bedt om.

          De to lagene ligger oppå hverandre i samme rute, så kolonnen ikke
          rykker når de bytter. Rødt er det som sletter, grønt er veien ut.
        */}
        <div className={`shot__rydd-boks${bekrefter ? " shot__rydd-boks--bekreft" : ""}`}>
          <button
            type="button"
            className="shot__rydd"
            onClick={() => setBekrefter(true)}
            disabled={merkede.length === 0}
          >
            Fjern alle merkede{merkede.length === 0 ? "" : ` (${merkede.length})`}
          </button>

          <div className="shot__bekreft">
            <button
              type="button"
              className="shot__bekreft-knapp shot__bekreft-knapp--ja"
              onClick={() => void fjernMerkede()}
            >
              Slett {merkede.length}
            </button>
            <button
              type="button"
              className="shot__bekreft-knapp shot__bekreft-knapp--nei"
              onClick={() => setBekrefter(false)}
            >
              Avbryt
            </button>
          </div>
        </div>

        <h2 className="shot__kolonnetittel">Scener</h2>
        <ul className="shot__sceneliste">
          {data.scener.map((s, i) => {
            const antall = perScene.get(s.id) ?? [];
            const igjen = antall.filter((b) => !b.gjort).length;
            return (
              <li
                key={s.id}
                ref={(el) => sceneStokk.settRad(s.id, el)}
                className={[
                  "shot__scenerad",
                  sceneBekreft === s.id ? "shot__scenerad--bekreft" : "",
                  sceneStokk.holdt === i ? "shot__scenerad--holdt" : "",
                ].filter(Boolean).join(" ")}
                style={sceneStokk.stil(i)}
              >
                {/* Tre streker, som i appens sideliste. Symbolet er hele
                    forklaringen på at raden kan flyttes. */}
                <span
                  className="shot__grep"
                  {...sceneStokk.haandtak(i)}
                  role="button"
                  aria-label={`Flytt scenen ${s.navn}`}
                >
                  <span /><span /><span />
                </span>
                <button
                  type="button"
                  className={`shot__scene${s.id === valgt ? " shot__scene--valgt" : ""}`}
                  onClick={() => { setValgt(s.id); setAapen(null); }}
                >
                  <span className="shot__scenenavn">{s.navn}</span>
                  {/* Tallet og papirkurven deler plass. Peker man på raden,
                      bytter de — da vokser ikke raden, og tallet er der
                      ellers. */}
                  <span className="shot__sceneantall mono">{igjen}</span>
                </button>

                <button
                  type="button"
                  className="shot__papirkurv"
                  onClick={() => setSceneBekreft(s.id)}
                  aria-label={`Slett scenen ${s.navn}`}
                  title="Slett scenen"
                >
                  <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                    <path
                      fill="none" stroke="currentColor" strokeWidth="1.3"
                      strokeLinecap="round" strokeLinejoin="round"
                      d="M2.5 4h11M6 4V2.6h4V4M4 4l.7 9.1a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L12 4M6.6 6.6v5M9.4 6.6v5"
                    />
                  </svg>
                </button>

                <div className="shot__bekreft shot__bekreft--scene">
                  <button
                    type="button"
                    className="shot__bekreft-knapp shot__bekreft-knapp--ja"
                    onClick={() => void slettScene(s.id)}
                  >
                    Slett
                  </button>
                  <button
                    type="button"
                    className="shot__bekreft-knapp shot__bekreft-knapp--nei"
                    onClick={() => setSceneBekreft(null)}
                  >
                    Avbryt
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {nyScene === null ? (
          <button
            type="button"
            className="shot__nyscene"
            onClick={() => setNyScene("")}
          >
            <span aria-hidden="true">+</span> Ny scene
          </button>
        ) : (
          <input
            className="shot__scenefelt"
            value={nyScene}
            autoFocus
            placeholder="Hva heter settingen?"
            onChange={(e) => setNyScene(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void lagScene(e.currentTarget.value);
              if (e.key === "Escape") setNyScene(null);
            }}
            onBlur={(e) => void lagScene(e.target.value)}
            aria-label="Navn på den nye scenen"
          />
        )}
      </aside>

      <section className="shot__liste">
        {scene === null ? (
          <div className="synk__melding">
            <strong>Ingen scener for opptaksdag {opptaksdag}.</strong>
            <span>
              Scenene er dagens settinger. Lag den første, så kan bildene
              legges inn under den.
            </span>
          </div>
        ) : (
          <>
            <header className="shot__hode">
              {/* Samme loddrette linje som hakene under, fordi den gjør det
                  samme som dem. Tittelen flytter seg etter. */}
              <button
                type="button"
                className={`shot__hake shot__hake--alle${
                  mine.length > 0 && mine.every((b) => b.gjort) ? " shot__hake--pa" : ""}`}
                onClick={() => void merkAlle(!(mine.length > 0 && mine.every((b) => b.gjort)))}
                disabled={mine.length === 0}
                aria-pressed={mine.length > 0 && mine.every((b) => b.gjort)}
                aria-label="Merk alle bildene i scenen"
                title="Merk alle"
              />
              <input
                className="shot__tittel"
                defaultValue={scene.navn}
                key={scene.id}
                onBlur={(e) => void dopScene(scene.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                aria-label="Navn på scenen"
              />
              <span className="shot__antall mono">{mine.filter((b) => !b.gjort).length}</span>
            </header>

            <ul className="shot__bilder">
              {mine.map((b, i) => (
                <li
                  key={b.id}
                  ref={(el) => shotStokk.settRad(b.id, el)}
                  className={[
                    "shot__bilde",
                    b.gjort ? "shot__bilde--gjort" : "",
                    shotStokk.holdt === i ? "shot__bilde--holdt" : "",
                  ].filter(Boolean).join(" ")}
                  style={shotStokk.stil(i)}
                >
                  <span
                    className="shot__grep"
                    {...shotStokk.haandtak(i)}
                    role="button"
                    aria-label={`Flytt «${b.tekst}»`}
                  >
                    <span /><span /><span />
                  </span>
                  <button
                    type="button"
                    className="shot__hake"
                    onClick={() => void endre(b.id, { gjort: !b.gjort })}
                    aria-pressed={b.gjort}
                    aria-label={b.gjort ? "Tatt" : "Ikke tatt"}
                  />

                  <div className="shot__felt">
                    {/*
                      VERKTØYET HØRER TIL SKRIVINGEN, ikke til raden.
                      Det hang på `onFocus` i hele linja før, og da sprang
                      det fram av at man trykket på haken — raden vokste
                      under fingeren, og neste trykk bommet på boksen fordi
                      den hadde flyttet seg. Nå åpner det seg bare når man
                      går inn for å skrive.
                    */}
                    <input
                      className="shot__linje"
                      defaultValue={b.tekst}
                      onFocus={() => setAapen(b.id)}
                      onBlur={(e) => void endre(b.id, { tekst: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      aria-label="Bildet"
                    />

                    {b.notat.length > 0 || aapen === b.id ? (
                      <input
                        className="shot__notat"
                        defaultValue={b.notat}
                        onFocus={() => setAapen(b.id)}
                        placeholder="Kommentar"
                        data-notat={b.id}
                        onBlur={(e) => void endre(b.id, { notat: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        aria-label="Kommentar"
                      />
                    ) : null}

                    {b.bildeSti === null ? null : (
                      <span className="shot__ramme">
                        <Referansebilde
                          key={`${b.bildeSti}:${bildeversjon[b.id] ?? 0}`}
                          sti={b.bildeSti}
                        />
                        {/* Krysset ligger PÅ bildet og viser seg når man er
                            borti det. En knapp i verktøylinja for noe som
                            står rett foran deg er en omvei. */}
                        <button
                          type="button"
                          className="shot__fjernbilde"
                          title="Fjern referansebildet"
                          aria-label="Fjern referansebildet"
                          onClick={() => {
                            const sti = b.bildeSti;
                            if (sti !== null) void sl.fjernBilde(sti);
                            void endre(b.id, { bildeSti: null });
                          }}
                        >
                          ×
                        </button>
                      </span>
                    )}

                    {opplasting?.id === b.id ? (
                      <span className={`shot__opplasting${opplasting.feil ? " shot__opplasting--feil" : ""}`}>
                        {opplasting.feil
                          ? "Fikk ikke lastet opp bildet. Prøv igjen."
                          : "Laster opp …"}
                      </span>
                    ) : null}

                    {aapen === b.id ? (
                      <div className="shot__verktoy">
                        <button
                          type="button"
                          className="shot__verktoyknapp"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const f = document.querySelector<HTMLInputElement>(
                              `[data-notat="${b.id}"]`,
                            );
                            f?.focus();
                          }}
                        >
                          Kommentar
                        </button>
                        {/*
                          EN ETIKETT, IKKE EN KNAPP som åpner en delt
                          filvelger. Etiketten er nettleserens egen vei inn
                          til filvelgeren: ingen `click()` å utløse, ingen
                          `dataset` å holde i takt, og linja den hører til
                          står i lukkingen. Den delte velgeren var det
                          feilen lå i.
                        */}
                        <label className="shot__verktoyknapp">
                          {b.bildeSti === null ? "Referansebilde" : "Bytt referansebilde"}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic"
                            className="shot__filvelger"
                            onChange={(e) => {
                              const fil = e.target.files?.[0];
                              e.target.value = "";
                              if (fil !== undefined) void lastOpp(fil, b.id);
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className="shot__verktoyknapp shot__verktoyknapp--bort"
                          onMouseDown={(e) => { e.preventDefault(); void slettShot(b.id); }}
                        >
                          Slett
                        </button>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}

              {/* Den tomme linja nederst. Man trykker og skriver — det er
                  hele poenget med at dette ikke er en tekstboks. */}
              <li className="shot__bilde shot__bilde--ny">
                <span className="shot__hake shot__hake--tom" aria-hidden="true" />
                <div className="shot__felt">
                  <input
                    className="shot__linje"
                    value={nyttShot}
                    placeholder={mine.length === 0 ? "Skriv det første bildet" : "Nytt bilde"}
                    onChange={(e) => setNyttShot(e.target.value)}
                    onFocus={() => { nyRef.current = null; setAapen("ny"); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        void leggTil(e.currentTarget.value);
                        nyRef.current = null;
                      }
                    }}
                    onBlur={(e) => { if (e.target.value.trim().length > 0) void materialiser(); }}
                    aria-label="Nytt bilde"
                  />

                  {/*
                    SAMME VALG HER SOM PÅ EN RAD SOM FINNES. Kommentaren og
                    referansebildet hører til bildet man holder på å skrive,
                    ikke til noe man må lagre først og gå inn i etterpå.
                    Trykket lager raden, og så gjør det det du ba om.
                  */}
                  {aapen === "ny" ? (
                    <div className="shot__verktoy">
                      <button
                        type="button"
                        className="shot__verktoyknapp"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          void (async () => {
                            const b = await materialiser();
                            if (b === null) return;
                            setAapen(b.id);
                            window.setTimeout(() => {
                              document.querySelector<HTMLInputElement>(
                                `[data-notat="${b.id}"]`,
                              )?.focus();
                            }, 0);
                          })();
                        }}
                      >
                        Kommentar
                      </button>
                      <label className="shot__verktoyknapp">
                        Referansebilde
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/heic"
                          className="shot__filvelger"
                          onChange={(e) => {
                            const fil = e.target.files?.[0];
                            e.target.value = "";
                            if (fil === undefined) return;
                            void (async () => {
                              const b = await materialiser();
                              if (b === null) return;
                              setAapen(b.id);
                              await lastOpp(fil, b.id);
                            })();
                          }}
                        />
                      </label>
                    </div>
                  ) : null}
                </div>
              </li>
            </ul>
          </>
        )}
      </section>

    </div>,
  );
}

/**
 * Referansebildet.
 *
 * Bøtta er ikke offentlig, så lenka må signeres — og den varer en time.
 * Hentes den her og ikke i lista, er det fordi en scene med tjue bilder
 * ellers ville bedt om tjue lenker før noe kom på skjermen.
 */
function Referansebilde({ sti }: { readonly sti: string }): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let avbrutt = false;
    void (async () => {
      const u = await sl.bildeUrl(sti);
      if (!avbrutt) setUrl(u);
    })();
    return () => { avbrutt = true; };
  }, [sti]);
  if (url === null) return null;
  return <img className="shot__referanse" src={url} alt="Referansebilde" />;
}
